import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import { ArrowForward, ArrowOutward } from '@mui/icons-material';
import { DEMO_REQUEST_MAILTO } from '../../config/contact';
import {
  marketingDisplayFont,
  marketingFinalBandSx,
  marketingPrimaryButtonSx,
} from './marketingSystem';

type SecondaryTarget =
  | { to: string; href?: undefined }
  | { href: string; to?: undefined };

export type MarketingFinalCtaProps = {
  /** Small monospace kicker line above the heading, e.g. "Platform · Next step". */
  kicker: string;
  /** The main CTA heading — usually a question or directive. */
  heading: string;
  /** One supporting paragraph. */
  body: string;
  /** Optional secondary action — renders as an inline text link. */
  secondary?: {
    label: string;
  } & SecondaryTarget;
};

/**
 * MarketingFinalCta
 * -----------------
 * Shared final-band CTA used at the bottom of every marketing page. Replaces
 * the earlier glass-card + pill + heading + two-button block with a lighter,
 * more confident composition: a monospace kicker with an accent bar, a large
 * display heading, a single primary button, and an optional inline text link.
 *
 * The content sits directly on the dark navy gradient band — no translucent
 * card wrapper — which keeps the type legible and the composition modern.
 */
const MarketingFinalCta: React.FC<MarketingFinalCtaProps> = ({
  kicker,
  heading,
  body,
  secondary,
}) => {
  const secondaryProps =
    secondary && 'to' in secondary && secondary.to !== undefined
      ? { component: RouterLink, to: secondary.to }
      : secondary && secondary.href !== undefined
        ? { component: 'a' as const, href: secondary.href }
        : null;

  return (
    <Box component="section" sx={{ ...marketingFinalBandSx, py: { xs: 7, md: 9 } }}>
      <Container maxWidth="md">
        <Box sx={{ maxWidth: 720 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
              mb: 2,
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 3,
                height: 16,
                borderRadius: 1,
                background: 'linear-gradient(180deg, #60a5fa, #3b82f6)',
              }}
            />
            <Typography
              component="span"
              sx={{
                fontFamily:
                  "'JetBrains Mono', 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
                fontSize: '0.72rem',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: '#bfdbfe',
                fontWeight: 400,
              }}
            >
              {kicker}
            </Typography>
          </Box>

          <Typography
            component="h2"
            sx={{
              fontFamily: marketingDisplayFont,
              fontSize: { xs: '2.1rem', md: '2.8rem' },
              fontWeight: 400,
              color: 'white',
              mb: 1.75,
              letterSpacing: '-0.035em',
              lineHeight: 1.08,
            }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{
              color: 'rgba(226,232,240,0.88)',
              mb: 3,
              lineHeight: 1.7,
              fontSize: { xs: '1rem', md: '1.05rem' },
              maxWidth: 620,
            }}
          >
            {body}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, md: 2.5 },
            }}
          >
            <Button
              component="a"
              href={DEMO_REQUEST_MAILTO}
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              sx={{
                ...marketingPrimaryButtonSx,
                px: 3.4,
                py: 1.6,
                fontSize: '1rem',
              }}
            >
              Request Demo
            </Button>
            {secondary && secondaryProps && (
              <Button
                {...secondaryProps}
                disableRipple
                endIcon={
                  <ArrowOutward sx={{ fontSize: '1rem', ml: -0.35 }} />
                }
                sx={{
                  color: '#dbeafe',
                  fontWeight: 400,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  px: 0.5,
                  py: 0.75,
                  letterSpacing: '-0.005em',
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  borderBottom: '1px solid rgba(147,197,253,0.28)',
                  '& .MuiButton-endIcon': {
                    transition: 'transform 160ms ease',
                  },
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: 'transparent',
                    borderBottomColor: 'rgba(255,255,255,0.72)',
                    '& .MuiButton-endIcon': {
                      transform: 'translate(2px, -2px)',
                    },
                  },
                }}
              >
                {secondary.label}
              </Button>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default MarketingFinalCta;
